import { useCallback } from 'react';

import { currentUser } from 'js/utils/user';
import {
  Comment,
  FollowableUser,
  NewsType,
  ReactionType,
  ResourcePayload,
  ResourceType,
  Tag,
} from 'js/social/shared/types';
import * as api from 'js/social/api';

export interface FeedCommentsActions {
  /**
   * Update the global users' state to reflect the new value of user
   * following.
   *
   * @param id ID of a target user
   * @param following Whether to follow the target user
   * @return void
   */
  setUserFollowing: (id: number, following: boolean) => void;
  /**
   * Remove all content from a user from being displayed in the feed.
   *
   * @param id ID of a user whose content is to be removed
   * @return void
   */
  removeUserContent: (id: number) => void;
  /**
   * Add a social comment to be displayed in the feed.
   *
   * @param type The type of commentable
   * @param commentableId The commentable object that the comment belongs to
   * @param parentCommentId The parent comment id of the comment, if any
   * @param comment The comment to be added
   * @param users The users associated with the comment
   * @return void
   */
  addFeedComment: (
    type: NewsType,
    commentableId: number | string,
    parentCommentId: number | string | null,
    comment: Comment,
    users: FollowableUser[],
  ) => void;
  /**
   * Remove a social comment  from being displayed in the feed.
   *
   * @param type The type of commentable
   * @param commentableId ID of the commentable of the target comment id
   * @param parentCommentId ID of the parent comment of the target
   * comment id, null if this is a top level comment
   * @param id ID of a target social comment
   * @return void
   */
  removeFeedComment: (
    type: NewsType,
    commentableId: number | string,
    parentCommentId: number | string | null,
    id: number | string,
  ) => void;
  /**
   * Update feed's state to reflect updated comment.
   * @param type The type of commentable
   * @param commentableId ID of the commentable of the target comment id
   * @param parentCommentId ID of the parent comment of the target
   * comment id, null if this is a top level comment
   * @param id ID of a target social comment
   * @param update The new comment state
   */
  updateFeedComment: (
    type: NewsType,
    commentableId: number | string,
    parentCommentId: number | string | null,
    id: number | string,
    update: Comment,
  ) => void;
  /**
   * Update feed's state to reflect user has liked comment
   *
   * @param type The type of commentable
   * @param commentableId ID of the commentable of the target comment id
   * @param parentCommentId ID of the parent comment of the target
   * comment id, null if this is a top level comment
   * @param id ID of a target social comment
   * @return void
   */
  likeFeedComment: (
    type: NewsType,
    commentableId: number | string,
    parentCommentId: number | string | null,
    id: number | string,
  ) => void;
  /**
   * Update feed's state to reflect user has unliked comment
   *
   * @param type The type of commentable
   * @param commentableId ID of the commentable of the target comment id
   * @param parentCommentId ID of the parent comment of the target
   * comment id, null if this is a top level comment
   * @param id ID of a target social comment
   * @return void
   */
  unlikeFeedComment: (
    type: NewsType,
    commentableId: number | string,
    parentCommentId: number | string | null,
    id: number | string,
  ) => void;
}

/**
 * This handles the various interactions a user can perform on a social
 * comment
 */
const useCommentInteractions = ({
  newsType,
  commentId,
  commentableId,
  parentCommentId,
  userId,
  tags,
  setUserFollowing,
  removeUserContent,
  addFeedComment,
  removeFeedComment,
  updateFeedComment,
  likeFeedComment,
  unlikeFeedComment,
}: {
  newsType: NewsType;
  commentId: number | string;
  commentableId: number | string;
  parentCommentId: number | string | null;
  userId: number;
  tags: Tag[];
} & FeedCommentsActions) => {
  const followUser = useCallback(async () => {
    await api.createUserFollowing(userId, 'social_news_feed', {
      item_id: commentId,
      item_type: ResourceType.Comment,
    });
    setUserFollowing(userId, true);
  }, [userId, commentId, setUserFollowing]);

  const unfollowUser = useCallback(async () => {
    await api.deleteUserFollowing(userId);
    setUserFollowing(userId, false);
  }, [userId, setUserFollowing]);

  const blockUser = useCallback(async () => {
    await api.createUserBlock(userId);
    removeUserContent(userId);
  }, [userId, removeUserContent]);

  const reportComment = useCallback(
    async (reason?: string) => {
      await api.reportComment(commentId, reason);
      removeFeedComment(newsType, commentableId, parentCommentId, commentId);
    },
    [newsType, commentableId, parentCommentId, commentId, removeFeedComment],
  );

  const deleteComment = useCallback(async () => {
    await api.deleteComment(commentId);
    removeFeedComment(newsType, commentableId, parentCommentId, commentId);
  }, [newsType, commentableId, parentCommentId, commentId, removeFeedComment]);

  const removeTag = useCallback(async () => {
    const comment = await api.deleteTag(commentId, ResourceType.Comment);
    updateFeedComment(
      newsType,
      commentableId,
      parentCommentId,
      commentId,
      comment,
    );
  }, [newsType, commentableId, parentCommentId, commentId, updateFeedComment]);

  const likeComment = useCallback(async () => {
    await api.createReaction(
      commentId,
      ResourceType.Comment,
      ReactionType.Like,
    );
    likeFeedComment(newsType, commentableId, parentCommentId, commentId);
  }, [newsType, commentableId, parentCommentId, commentId, likeFeedComment]);

  const unlikeComment = useCallback(async () => {
    await api.deleteReaction(
      commentId,
      ResourceType.Comment,
      ReactionType.Like,
    );
    unlikeFeedComment(newsType, commentableId, parentCommentId, commentId);
  }, [newsType, commentableId, parentCommentId, commentId, unlikeFeedComment]);

  const createReply = useCallback(
    async ({ content, tags }: ResourcePayload) => {
      const { comment, user } = await api.createComment(
        content,
        tags,
        commentableId,
        newsType,
        parentCommentId || commentId,
      );
      addFeedComment(
        newsType,
        commentableId,
        parentCommentId || commentId,
        comment,
        [user],
      );
    },
    [newsType, commentableId, parentCommentId, commentId, addFeedComment],
  );

  const isItemOwner = userId === currentUser?.id;
  const isLoggedIn = currentUser != null;
  const isTagged = tags.some(
    (tag) => tag.tagType === 'user' && tag.tagId === currentUser?.id,
  );

  return {
    actions: {
      followUser,
      unfollowUser,
      blockUser,
      reportComment,
      deleteComment,
      removeTag,
      likeComment,
      unlikeComment,
      createReply,
    },
    permissions: {
      canFollow: isLoggedIn && !isItemOwner,
      canBlock: isLoggedIn && !isItemOwner,
      canReport: isLoggedIn && !isItemOwner,
      canDelete: isItemOwner,
      canUntag: isTagged,
    },
  };
};

export default useCommentInteractions;
